import React, { useState, useEffect } from 'react';
import { Calendar, Loader2, AlertCircle } from 'lucide-react';
import { formatCurrency } from '../../utils/formatCurrency';
import SortableTableHeader from '../../components/SortableTableHeader';
import { getSalesByProductSummary, getSalesByProductDetail } from '../../utils/reportApi';
import type { SalesByProductSummaryReport, SalesByProductDetailReport } from '../../types/report';
import { ReportLayout } from '../../components/reports/ReportLayout';
import AsyncItemSelect from '../../components/AsyncItemSelect';
import type { Item } from '../../types/item';
import { useInvoicePreviewModal } from '../../hooks/useInvoicePreviewModal';

type DetailSortColumn = 'invoice_number' | 'txn_date' | 'customer_name' | 'qty' | 'line_total';

const SalesByProductDetailView = ({ itemId, itemDescription, startDate, endDate }: { itemId: number | null; itemDescription: string | null; itemName: string; startDate: string; endDate: string }) => {
  const [detail, setDetail] = useState<SalesByProductDetailReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { openInvoicePreview, invoicePreviewModal } = useInvoicePreviewModal();

  const [sortColumn, setSortColumn] = useState<DetailSortColumn>('txn_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (column: DetailSortColumn) => {
    if (sortColumn === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDir('asc');
    }
  };

  const sortedLines = React.useMemo(() => {
    const lines = detail?.products?.[0]?.lines || [];
    return [...lines].sort((a, b) => {
      let comparison = 0;
      if (sortColumn === 'invoice_number') comparison = a.invoice_number.localeCompare(b.invoice_number);
      else if (sortColumn === 'txn_date') comparison = String(a.txn_date).localeCompare(String(b.txn_date));
      else if (sortColumn === 'customer_name') comparison = a.customer_name.localeCompare(b.customer_name);
      else if (sortColumn === 'qty') comparison = parseFloat(a.qty) - parseFloat(b.qty);
      else if (sortColumn === 'line_total') comparison = parseFloat(a.line_total) - parseFloat(b.line_total);
      
      return sortDir === 'asc' ? comparison : -comparison;
    });
  }, [detail, sortColumn, sortDir]);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setLoading(true);
        const data = await getSalesByProductDetail({ 
          item_id: itemId || undefined,
          description: itemId === null && itemDescription ? itemDescription : undefined,
          start_date: startDate, 
          end_date: endDate
        });
        setDetail(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load details');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [itemId, itemDescription, startDate, endDate]);

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-4" />
        <p className="text-slate-500 font-medium">Loading details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-4" />
        <p className="text-red-600 font-medium">{error}</p>
      </div>
    );
  }

  if (!detail || !detail.products[0]) {
    return <div className="p-8 text-center text-slate-500">No data found.</div>;
  }

  return (
    <>
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
          <tr>
            <SortableTableHeader<DetailSortColumn> label="Invoice #" column="invoice_number" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} />
            <SortableTableHeader<DetailSortColumn> label="Txn Date" column="txn_date" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} />
            <SortableTableHeader<DetailSortColumn> label="Customer" column="customer_name" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} />
            <SortableTableHeader<DetailSortColumn> label="Qty" column="qty" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="right" />
            <SortableTableHeader<DetailSortColumn> label="Amount" column="line_total" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="right" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sortedLines.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                No invoices found for this period.
              </td>
            </tr>
          ) : (
            sortedLines.map(line => (
              <tr key={`${line.invoice_id}-${line.line_number}`} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4 font-medium">
                  <button
                    type="button"
                    onClick={() => openInvoicePreview(line.invoice_id)}
                    className="text-blue-700 hover:underline text-left"
                  >
                    {line.invoice_number}
                  </button>
                </td>
                <td className="px-6 py-4 text-slate-600">{line.txn_date}</td>
                <td className="px-6 py-4 text-slate-600">{line.customer_name}</td>
                <td className="px-6 py-4 text-right text-slate-600 font-medium">
                  {parseFloat(line.qty).toString()}
                </td>
                <td className="px-6 py-4 text-right text-slate-600 font-medium">
                  {formatCurrency(parseFloat(line.line_total))}
                </td>
              </tr>
            ))
          )}
        </tbody>
        <tfoot className="bg-slate-50 border-t border-slate-200">
          <tr>
            <td colSpan={3} className="px-6 py-4 font-semibold text-slate-900 text-right">
              Total Sales
            </td>
            <td className="px-6 py-4 text-right font-semibold text-slate-900 text-base">
              {parseFloat(detail.totals.total_qty).toString()}
            </td>
            <td className="px-6 py-4 text-right font-semibold text-slate-900 text-base">
              {formatCurrency(parseFloat(detail.totals.total_sales))}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
    {invoicePreviewModal}
    </>
  );
};

type SummarySortColumn = 'product' | 'invoices' | 'qty' | 'total_sales';

const SalesByProductSummary = () => {
  const defaultEndDate = new Date().toISOString().split('T')[0];
  const defaultStartDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState<string>(defaultStartDate);
  const [endDate, setEndDate] = useState<string>(defaultEndDate);
  const [filterItemId, setFilterItemId] = useState<number | null>(null);
  const [filterItemName, setFilterItemName] = useState<string>('');
  
  const [report, setReport] = useState<SalesByProductSummaryReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sortColumn, setSortColumn] = useState<SummarySortColumn>('product');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: SummarySortColumn) => {
    if (sortColumn === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDir('asc');
    }
  };

  const sortedProducts = React.useMemo(() => {
    if (!report) return [];
    return [...report.products].sort((a, b) => {
      let comparison = 0;
      if (sortColumn === 'product') {
        comparison = a.item_name.localeCompare(b.item_name);
      } else if (sortColumn === 'invoices') {
        comparison = a.invoice_count - b.invoice_count;
      } else if (sortColumn === 'qty') {
        comparison = parseFloat(a.total_qty) - parseFloat(b.total_qty);
      } else if (sortColumn === 'total_sales') {
        comparison = parseFloat(a.total_sales) - parseFloat(b.total_sales);
      }
      return sortDir === 'asc' ? comparison : -comparison;
    });
  }, [report, sortColumn, sortDir]);

  // Drill-down state
  const [view, setView] = useState<'summary' | 'detail'>('summary');
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [selectedItemName, setSelectedItemName] = useState<string | null>(null);

  useEffect(() => {
    if (view === 'summary') {
      const fetchReport = async () => {
        try {
          setLoading(true);
          const data = await getSalesByProductSummary({ 
            start_date: startDate, 
            end_date: endDate,
            item_id: filterItemId || undefined
          });
          setReport(data);
          setError(null);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load report');
        } finally {
          setLoading(false);
        }
      };
      fetchReport();
    }
  }, [startDate, endDate, view, filterItemId]);

  const handleDrillDown = (itemId: number | null, itemName: string) => {
    setSelectedItemId(itemId);
    setSelectedItemName(itemName);
    setView('detail');
  };

  const handleBackToSummary = () => {
    setView('summary');
    setSelectedItemId(null);
    setSelectedItemName(null);
  };

  const filters = (
    <div className="flex flex-col sm:flex-row sm:items-end gap-4 w-full md:w-auto">
      <div className="flex-1 min-w-[200px] max-w-xs">
        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">
          Item
        </label>
        <AsyncItemSelect
          value={filterItemId || 0}
          selectedLabel={filterItemName}
          onSelect={(item: Item) => {
            setFilterItemId(item.id);
            setFilterItemName(item.name);
            if (view === 'detail') handleBackToSummary();
          }}
          onClear={() => {
            setFilterItemId(null);
            setFilterItemName('');
            if (view === 'detail') handleBackToSummary();
          }}
          placeholder="All items"
        />
      </div>
      <div className="flex-1 max-w-xs">
        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">
          Start Date
        </label>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              if (view === 'detail') handleBackToSummary();
            }}
            className="w-full pl-10 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400"
          />
        </div>
      </div>
      <div className="flex-1 max-w-xs">
        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">
          End Date
        </label>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              if (view === 'detail') handleBackToSummary();
            }}
            className="w-full pl-10 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400"
          />
        </div>
      </div>
    </div>
  );

  return (
    <ReportLayout
      title={view === 'summary' ? 'Sales by Product Summary' : `Sales by Product Detail`}
      description={view === 'summary' ? 'View quantity and revenue per item over a specific period.' : `Sales for ${selectedItemName}`}
      filters={filters}
      onBack={view === 'detail' ? handleBackToSummary : undefined}
      backLabel="Back to summary"
      onExport={() => alert('Export functionality to be implemented')}
      onPrint={() => window.print()}
    >
      {view === 'summary' ? (
        loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-4" />
            <p className="text-slate-500 font-medium">Loading report data...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-4" />
            <p className="text-red-600 font-medium">{error}</p>
          </div>
        ) : !report ? (
          <div className="p-8 text-center text-slate-500">No data available.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                <tr>
                  <SortableTableHeader<SummarySortColumn> label="Product / Service" column="product" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} />
                  <SortableTableHeader<SummarySortColumn> label="Invoices" column="invoices" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="center" />
                  <SortableTableHeader<SummarySortColumn> label="Qty Sold" column="qty" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="right" />
                  <SortableTableHeader<SummarySortColumn> label="Total Sales" column="total_sales" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedProducts.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                      No sales found for this period.
                    </td>
                  </tr>
                ) : (
                  sortedProducts.map(product => (
                    <tr key={product.item_id || product.item_name} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-slate-900">
                        {product.item_name}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium border border-slate-200">
                          {product.invoice_count}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {parseFloat(product.total_qty).toString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleDrillDown(product.item_id, product.item_name)}
                          className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-base"
                          title="Click to view details"
                        >
                          {formatCurrency(parseFloat(product.total_sales))}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <td className="px-6 py-4 font-semibold text-slate-900">
                    Total ({report.totals.product_count || report.products.length} Products)
                  </td>
                  <td></td>
                  <td className="px-6 py-4 text-right font-semibold text-slate-900 text-base">
                    {parseFloat(report.totals.total_qty).toString()}
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-slate-900 text-lg">
                    {formatCurrency(parseFloat(report.totals.total_sales))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      ) : (
        selectedItemName && (
          <SalesByProductDetailView 
            itemId={selectedItemId} 
            itemDescription={selectedItemId === null ? selectedItemName : null}
            itemName={selectedItemName} 
            startDate={startDate}
            endDate={endDate}
          />
        )
      )}
    </ReportLayout>
  );
};

export default SalesByProductSummary;
